import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';

const Syllabus = () => {
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedSemester, setSelectedSemester] = useState('all');

  useEffect(() => {
    fetchSyllabi();
  }, []);

  const fetchSyllabi = async () => {
    try {
      const response = await fetch('/admin/api/syllabus.php');
      const data = await response.json();
      setSyllabi(data);
    } catch (error) {
      console.error('Error fetching syllabi:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSyllabi = syllabi.filter(syllabus => {
    const gradeMatch = selectedGrade === 'all' || syllabus.grade === selectedGrade;
    const subjectMatch = selectedSubject === 'all' || syllabus.subject === selectedSubject;
    const semesterMatch = selectedSemester === 'all' || syllabus.semester === selectedSemester;
    return gradeMatch && subjectMatch && semesterMatch;
  });

  const uniqueGrades = [...new Set(syllabi.map(syllabus => syllabus.grade))];
  const uniqueSubjects = [...new Set(syllabi.map(syllabus => syllabus.subject))];
  const uniqueSemesters = [...new Set(syllabi.map(syllabus => syllabus.semester))];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Academic Syllabus</h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Access comprehensive syllabi for all grades, subjects, and semesters. 
              Download detailed curriculum outlines to stay on track with your studies.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Grade
              </label>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Grades</option>
                {uniqueGrades.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Subjects</option>
                {uniqueSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Semester
              </label>
              <select
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="all">All Semesters</option>
                {uniqueSemesters.map(semester => (
                  <option key={semester} value={semester}>{semester}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Syllabus Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {filteredSyllabi.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="fas fa-book-open"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Syllabus Found</h3>
              <p className="text-gray-600">No syllabi match your current filters.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSyllabi.map((syllabus) => (
              <div
                key={syllabus.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      syllabus.publish_status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {syllabus.publish_status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(syllabus.created_at)}
                    </span>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {syllabus.title}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-graduation-cap w-4 h-4 mr-2"></i>
                      <span className="text-sm">Grade {syllabus.grade}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-book w-4 h-4 mr-2"></i>
                      <span className="text-sm">{syllabus.subject}</span>
                    </div>
                    
                    <div className="flex items-center text-gray-600">
                      <i className="fas fa-calendar-alt w-4 h-4 mr-2"></i>
                      <span className="text-sm">{syllabus.semester}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {syllabus.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      <i className="fas fa-file-pdf mr-1"></i>
                      {syllabus.file_path ? 'PDF Available' : 'No File'}
                    </div>
                    
                    <div className="flex space-x-2">
                      {syllabus.file_path && (
                        <a
                          href={`admin/${syllabus.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors"
                        >
                          <i className="fas fa-download mr-1"></i>Download
                        </a>
                      )}
                      <button className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded text-xs font-medium transition-colors">
                        <i className="fas fa-eye mr-1"></i>View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Study Tips */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <i className="fas fa-lightbulb text-purple-600 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">
                Study Tips
              </h3>
              <ul className="text-purple-800 space-y-1">
                <li>• Download and review your syllabus at the beginning of each semester</li>
                <li>• Create a study schedule based on the syllabus timeline</li>
                <li>• Keep track of important dates and deadlines</li>
                <li>• Use the syllabus as a roadmap for your academic progress</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Syllabus;
